import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './UpperBar.css';
import frenchFlag from '../../../images/fr.png';
import englishFlag from '../../../images/en.png';
import arabicFlag from '../../../images/ar.png';
import { setSelectedRole } from '../../../redux/actions/roleActions';
import { logout } from '../../../redux/actions/authActions';
import {
    getRecentNotificationsRequest,
    getUnreadCountRequest,
    markAllNotificationsAsReadRequest,
    markNotificationAsReadRequest,
} from '../../../redux/actions/notificationActions';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import {API_BASE_URL} from "../../../../api";
import {
    readNavigationSelection,
    saveNavigationSelectionForModule,
    saveNavigationSelectionForRole,
} from "../../../utils/navigationSelectionStorage";

const getUserInitials = (user) => {
    const fullNameParts = (user?.fullName || "").trim().split(/\s+/).filter(Boolean);
    const firstName = user?.firstName || user?.prenom || fullNameParts[0] || "";
    const lastName = user?.lastName || user?.nom || (fullNameParts.length > 1 ? fullNameParts[fullNameParts.length - 1] : "") || "";
    return `${lastName.charAt(0)}${firstName.charAt(0)}`.toUpperCase() || "U";
};

const getRoleText = (role) => `${role?.name || ''} ${role?.code || ''}`.toUpperCase();

const formatNotificationTime = (value) => {
    if (!value) return "";
    const createdAt = new Date(value);
    if (Number.isNaN(createdAt.getTime())) return "";

    const diffMinutes = Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 60000));
    if (diffMinutes < 1) return "A l'instant";
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Il y a ${diffHours} h`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Il y a ${diffDays} j`;

    return createdAt.toLocaleDateString('fr-FR');
};

const mapNotificationForUpperBar = (notification) => ({
    id: notification.id,
    title: notification.title,
    module: notification.moduleName,
    message: notification.message || notification.title,
    time: formatNotificationTime(notification.createdAt),
    unread: !notification.read,
    link: notification.link,
    source: 'dynamic',
});

const getDemoNotifications = (moduleCode, role) => {
    const roleText = getRoleText(role);
    const isFormationResponsable =
        roleText.includes('RESPONSABLE FORMATION') ||
        roleText.includes('FORMATION_RESP') ||
        roleText.includes('TRAINING_RESPONSABLE');
    const isCollaborateur =
        roleText.includes('COLLABORATEUR') ||
        roleText.includes('EMPLOYEE') ||
        roleText.includes('APPRENANT');

    // if (moduleCode === 'FORMATION' && isFormationResponsable) {
    //     return [
    //         {
    //             id: 'demo-formation-inscription-1',
    //             module: 'Formation',
    //             message: "Nezha EL AMRANI a fait une demande d'inscription à une formation à son collaborateur Farisi Ahmed.",
    //             time: 'Il y a 12 min',
    //             unread: true,
    //         },
    //         {
    //             id: 'demo-formation-evenement-1',
    //             module: 'Formation',
    //             message: "Fatima Bouzaouit a fait une demande d'inscription à un événement à son collaborateur Said Cherkaoui.",
    //             time: 'Il y a 32 min',
    //             unread: true,
    //         },
    //     ];
    // }

    if (moduleCode === 'SKILLS' && isCollaborateur) {
        return [
            {
                id: 'demo-skills-collab-1',
                module: 'Compétences',
                message: 'Votre évaluation de compétences a été traitée par votre manager.',
                time: 'Il y a 18 min',
                unread: true,
            },
        ];
    }

    return [];
};

const normalizeAppPath = (path) => {
    if (!path || typeof path !== "string") return "";
    const trimmed = path.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const getRolePath = (role) => {
    const roleName = `${role?.name || ''} ${role?.code || ''}`.toUpperCase();
    const compactRoleName = roleName.replace(/\s+/g, '');
    if (Number(role?.id) === 54 || compactRoleName.includes('RESPONSABLEONBOARDING')) return '/recrutement/onboarding/configuration';
    if (Number(role?.id) === 53 || compactRoleName.includes('NRONBOARDING')) return '/collaborateur/onboarding';
    const explicitPath = normalizeAppPath(role?.routePath || role?.modulePath || role?.module_path);
    if (explicitPath) return explicitPath;
    return '';
};
const isNewRecruitRole = (role) => Number(role?.id) === 53;
const getPreferredModuleRole = (roles = [], moduleId) => {
    const moduleRoles = (roles || []).filter((role) => String(role.moduleId) === String(moduleId));
    return moduleRoles.find((role) => role?.isDefault)
        || moduleRoles.find((role) => isNewRecruitRole(role) && getRolePath(role))
        || moduleRoles.find((role) => !isNewRecruitRole(role) && getRolePath(role))
        || moduleRoles.find((role) => getRolePath(role))
        || moduleRoles[0];
};

const getModuleCodeFromPath = (path = "") => {
    if (path.startsWith('/skills')) return 'SKILLS';
    if (path.startsWith('/formation')) return 'FORMATION';
    if (path.startsWith('/performance')) return 'PERF';
    if (path.startsWith('/recrutement')) return 'RECRUTEMENT';
    if (path.startsWith('/handbook')) return 'HANDBOOK';
    if (path.startsWith('/collaborateur')) return 'SELFCARE';
    return '';
};

const roleMatchesModuleCode = (role, moduleCode) => {
    const normalizedCode = String(moduleCode || '').trim().toUpperCase();
    const roleModuleCode = String(role?.moduleCode || role?.module?.code || '').trim().toUpperCase();
    const roleModuleName = String(role?.moduleName || role?.module?.name || '').trim().toUpperCase();
    const roleCode = String(role?.code || '').trim().toUpperCase();

    return roleModuleCode === normalizedCode ||
        roleModuleName === normalizedCode ||
        roleCode === normalizedCode;
};

const isSameRole = (left, right) => {
    if (!left || !right) return false;
    if (left.id != null && right.id != null) {
        return String(left.id) === String(right.id);
    }
    if (left.code && right.code) {
        return String(left.code) === String(right.code);
    }
    return String(left.name || '') === String(right.name || '');
};

const resolvePersistedRole = (roles = [], persistedSelection, currentUserId) => {
    if (!Array.isArray(roles) || roles.length === 0) return null;

    const persistedUserId = persistedSelection?.userId;
    if (persistedUserId && currentUserId && String(persistedUserId) !== String(currentUserId)) {
        return null;
    }

    const persistedRole = persistedSelection?.role;

    if (persistedRole?.id != null) {
        const byId = roles.find((role) => String(role.id) === String(persistedRole.id));
        if (byId) return byId;
    }

    if (persistedRole?.code) {
        const byCode = roles.find((role) => String(role.code) === String(persistedRole.code));
        if (byCode) return byCode;
    }

    if (persistedRole?.name) {
        const byName = roles.find((role) => String(role.name) === String(persistedRole.name));
        if (byName) return byName;
    }

    return null;
};

const getFirstVisibleMenuRoute = (menus = []) => {
    if (!Array.isArray(menus) || menus.length === 0) return "";

    const visibleMenus = menus.filter((menu) => menu?.displayStatus === "VISIBLE");
    const sortByDisplayOrder = (a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0);
    const roots = visibleMenus
        .filter((menu) => !menu.parentMenuId)
        .sort(sortByDisplayOrder);

    for (const root of roots) {
        const children = visibleMenus
            .filter((menu) => String(menu.parentMenuId) === String(root.id))
            .sort(sortByDisplayOrder);

        if (children.length > 0) {
            const childRoute = children
                .map((menu) => normalizeAppPath(menu.routePath))
                .find(Boolean);
            if (childRoute) return childRoute;
        } else {
            const rootRoute = normalizeAppPath(root.routePath);
            if (rootRoute) return rootRoute;
        }
    }

    return visibleMenus
        .sort(sortByDisplayOrder)
        .map((menu) => normalizeAppPath(menu.routePath))
        .find(Boolean) || "";
};

const sortModulesByDisplayOrder = (modules = []) =>
    [...modules].sort((left, right) =>
        (left.displayOrder ?? 9999) - (right.displayOrder ?? 9999) ||
        String(left.name || "").localeCompare(String(right.name || ""))
    );

const UpperBar = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [isModulesOpen, setIsModulesOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isModuleSwitcherOpen, setIsModuleSwitcherOpen] = useState(false);
    const [readDemoNotificationIds, setReadDemoNotificationIds] = useState([]);

    const modulesRef = useRef(null);
    const notificationsRef = useRef(null);
    const userDropdownRef = useRef(null);
    const moduleSwitcherRef = useRef(null);

    const selectedRole = useSelector(state => state.role?.selectedRole);
    const currentUser = useSelector(state => state.auth?.user || state.user?.currentUser);
    const [profileImageFailed, setProfileImageFailed] = useState(false);
    const [availableModules, setAvailableModules] = useState([]);

    const profilePhotoSrc = currentUser && currentUser.profilePictureUrl
        ? `${API_BASE_URL}/api/users/images/profile/${currentUser.profilePictureUrl}`
        : "";
    const hasProfilePhoto = !!profilePhotoSrc && !profileImageFailed;
    const userInitials = getUserInitials(currentUser);

    useEffect(() => {
        setProfileImageFailed(false);
    }, [currentUser?.profilePictureUrl]);

    const allRoles = currentUser?.roles || [];

    useEffect(() => {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        const companyId = currentUser?.companyId || sessionStorage.getItem('selectedCompanyId');
        const uid = currentUser?.id || sessionStorage.getItem('uid');

        if (!token || !companyId) return;

        fetch(`${API_BASE_URL}/api/modules`, {
            headers: {
                Authorization: `Bearer ${token}`,
                uid: uid ? String(uid) : '',
                'X-Tenant-Id': String(companyId),
            },
        })
            .then((response) => (response.ok ? response.json() : []))
            .then((data) => setAvailableModules(Array.isArray(data) ? data : []))
            .catch(() => setAvailableModules([]));
    }, [currentUser?.companyId, currentUser?.id]);

    useEffect(() => {
        if (allRoles.length === 0) return;

        const currentPath = location.pathname;
        const pathModuleCode = getModuleCodeFromPath(currentPath);

        const persistedSelection = readNavigationSelection();
        const restoredRole = resolvePersistedRole(allRoles, persistedSelection, currentUser?.id);

        let targetRole = null;

        if (pathModuleCode) {
            if (selectedRole && roleMatchesModuleCode(selectedRole, pathModuleCode)) {
                targetRole = selectedRole;
            } else if (restoredRole && roleMatchesModuleCode(restoredRole, pathModuleCode)) {
                targetRole = restoredRole;
            } else {
                targetRole = allRoles.find((role) => roleMatchesModuleCode(role, pathModuleCode))
                    || (persistedSelection?.module?.id && getPreferredModuleRole(allRoles, persistedSelection.module.id))
                    || allRoles[0];
            }
        } else {
            if (restoredRole) {
                targetRole = restoredRole;
            } else if (selectedRole) {
                targetRole = resolvePersistedRole(allRoles, { role: selectedRole }, currentUser?.id) || selectedRole;
            } else {
                const persistedModuleId = persistedSelection?.module?.id;
                targetRole = (persistedModuleId != null && getPreferredModuleRole(allRoles, persistedModuleId)) || allRoles[0];
            }
        }

        if (targetRole && (!selectedRole || !isSameRole(selectedRole, targetRole))) {
            dispatch(setSelectedRole({ ...targetRole, userId: currentUser?.id }));
            saveNavigationSelectionForRole(targetRole, currentUser?.id);
        }
    }, [allRoles, currentUser?.id, location.pathname, dispatch]);

    const userRoles = allRoles.filter(
        role => String(role.moduleId) === String(selectedRole?.moduleId)
    );

    const defaultRole = userRoles?.find(role => role?.isDefault) || userRoles[0];

    const displayedRole = selectedRole || defaultRole;

    useEffect(() => {
        const isRTL = i18n.language === 'ar';
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
        document.documentElement.setAttribute('lang', i18n.language);
    }, [i18n.language]);

    /* ── Module Switcher data ── */
    const MODULE_ICONS = {
        FORMATION:   { emoji: '📚', bg: '#DBEAFE' },
        SKILLS:      { emoji: '🎯', bg: '#EDE9FE' },
        PERF:        { emoji: '📊', bg: '#D1FAE5' },
        RECRUTEMENT: { emoji: '👥', bg: '#FEF3C7' },
        HANDBOOK:    { emoji: '📖', bg: '#FEE2E2' },
        SELFCARE:    { emoji: '💙', bg: '#E0F2FE' },
    };

    const MODULE_ROUTES = {
        SELFCARE:    '/collaborateur/dashboard',
        SKILLS:      '/skills/dashboard/collaborateur',
        FORMATION:   '/formation/dashboard/collaborateur',
        PERF:        '/performance/dashboard/collaborateur',
        RECRUTEMENT: '/recrutement/dashboard/collaborateur',
        HANDBOOK:    '/handbook/dashboard/collaborateur',
    };

    const getModuleImageUrl = (picture) =>
        picture ? `${API_BASE_URL}/api/modules/images/${picture}` : "";

    const userModulesForSwitcher = (() => {
        const map = new Map();
        const modulesById = new Map((availableModules || []).map((module) => [String(module.id), module]));

        (availableModules || []).forEach((module) => {
            if (!module?.id) return;
            map.set(String(module.id), {
                ...module,
                id: module.id,
                name: module.name,
                code: module.code,
                routePath: module.routePath || module.modulePath || module.module_path,
                picture: module.picture || module.modulePicture || module.moduleImage || "",
                displayOrder: module.displayOrder,
            });
        });

        (currentUser?.roles || []).forEach(role => {
            if (role.moduleId) {
                const fullModule = modulesById.get(String(role.moduleId)) || {};
                map.set(String(role.moduleId), {
                    ...map.get(String(role.moduleId)),
                    id: role.moduleId,
                    name: fullModule.name || role.moduleName || role.name,
                    code: String(fullModule.code || role.moduleCode || role.code || '').trim(),
                    roleName: role.name,
                    routePath: fullModule.routePath || role.routePath || role.modulePath || role.module_path,
                    picture: fullModule.picture || role.modulePicture || role.moduleImage || role.picture || "",
                    displayOrder: fullModule.displayOrder,
                });
            }
        });
        return sortModulesByDisplayOrder(Array.from(map.values()).filter((module) => module.name));
    })();
    const handleModuleSwitcherClick = async (module) => {
        const targetRole = getPreferredModuleRole(currentUser?.roles, module.id);
        if (targetRole) {
            saveNavigationSelectionForRole(targetRole, currentUser?.id);
            dispatch(setSelectedRole({ ...targetRole, userId: currentUser?.id }));
            const route = await getRoleDefaultRoute(targetRole);
            if (route) {
                navigate(route);
            } else {
                const fallbackRoute = getRolePath(targetRole) ||
                    normalizeAppPath(module.routePath || module.modulePath || module.module_path) ||
                    MODULE_ROUTES[module.code] ||
                    '/collaborateur/dashboard';
                navigate(fallbackRoute);
            }
        } else {
            saveNavigationSelectionForModule(module, null, currentUser?.id);
        }
        setIsModuleSwitcherOpen(false);
        setIsModulesOpen(false);
    };

    const currentPath = location.pathname;
    const routeModuleCode = getModuleCodeFromPath(currentPath);
    const activeModule = userModulesForSwitcher.find((module) => routeModuleCode && module.code === routeModuleCode)
        || userModulesForSwitcher.find((module) => {
            const route = normalizeAppPath(module.routePath || module.modulePath || module.module_path) ||
                MODULE_ROUTES[module.code];
            return route && currentPath.startsWith(route);
        }) || userModulesForSwitcher.find((module) =>
            selectedRole?.moduleId && String(module.id) === String(selectedRole.moduleId)
        );
    const activeModuleCode = activeModule?.code || routeModuleCode || Object.entries(MODULE_ROUTES)
        .find(([, path]) => currentPath.startsWith(path))?.[0];
    const activeModuleLabel = activeModule?.name || activeModuleCode;
    const notificationModuleCode = routeModuleCode || activeModuleCode;
    const notificationModuleId = displayedRole?.moduleId || activeModule?.id;
    const notificationState = useSelector(state => state.notification || {});
    const dynamicNotifications = (notificationState.unreadNotifications || []).map(mapNotificationForUpperBar);
    const demoNotifications = getDemoNotifications(notificationModuleCode, displayedRole).map((notification) => ({
        ...notification,
        unread: notification.unread && !readDemoNotificationIds.includes(notification.id),
        source: 'demo',
    }));
    const notifications = [...dynamicNotifications, ...demoNotifications];
    const unreadCount = notifications.filter(n => n.unread).length;

    /* ── Notifications ── */
    useEffect(() => {
        if (!currentUser?.id || !displayedRole?.id || !notificationModuleId) return;
        dispatch(getRecentNotificationsRequest(currentUser.id, displayedRole.id, notificationModuleId));
        dispatch(getUnreadCountRequest(currentUser.id, displayedRole.id, notificationModuleId));
    }, [currentUser?.id, displayedRole?.id, notificationModuleId, dispatch]);

    const modules = [
        { name: 'Core RH',           description: 'Centralisez facilement vos données RH',     icon: '📁', color: '#60A5FA', bgColor: '#DBEAFE' },
        { name: 'Congés et absences', description: 'Gérez mieux vos demandes de congés',        icon: '🏝️', color: '#F87171', bgColor: '#FEE2E2' },
        { name: 'Temps et activités', description: 'Déclarez facilement vos timesheets',         icon: '🕐', color: '#A78BFA', bgColor: '#EDE9FE' },
        { name: 'Notes de frais',     description: 'Simplifiez la gestion de vos dépenses',     icon: '🧾', color: '#34D399', bgColor: '#D1FAE5' },
        { name: 'Télétravail',        description: 'Maîtrisez votre charte de télétravail',     icon: '💻', color: '#60A5FA', bgColor: '#DBEAFE' },
        { name: 'Bulletins de paie',  description: 'Dématérialisez vos bulletins de paie',      icon: '📄', color: '#FBBF24', bgColor: '#FEF3C7' },
    ];

    const roleIcons = {
        'SUPER_ADMIN': '👑', 'ADMIN': '👔', 'FORMATION_RESP': '📚',
        'SKILLS_MANAGER': '🎯', 'HR_MANAGER': '👨‍💼', 'EMPLOYEE': '👤',
        'Responsable RH': '👨‍💼', 'Collaborateur': '👤', 'Manager': '👔',
        'Admin': '⚙️', 'Meta admin': '👑', 'DEFAULT': '👤',
    };
    const getRoleIcon = (roleName) => roleIcons[roleName] || roleIcons['DEFAULT'];

    /* ── Click outside ── */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modulesRef.current && !modulesRef.current.contains(event.target))
                setIsModulesOpen(false);
            if (notificationsRef.current && !notificationsRef.current.contains(event.target))
                setIsNotificationsOpen(false);
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target))
                setIsUserDropdownOpen(false);
            if (moduleSwitcherRef.current && !moduleSwitcherRef.current.contains(event.target))
                setIsModuleSwitcherOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const markAsRead = (notif) => {
        if (notif.source === 'demo') {
            setReadDemoNotificationIds((ids) => ids.includes(notif.id) ? ids : [...ids, notif.id]);
            return;
        }
        dispatch(markNotificationAsReadRequest(notif.id));
    };
    const markAllAsRead = () => {
        const unreadDemoIds = notifications
            .filter((notification) => notification.source === 'demo' && notification.unread)
            .map((notification) => notification.id);
        if (unreadDemoIds.length > 0) {
            setReadDemoNotificationIds((ids) => Array.from(new Set([...ids, ...unreadDemoIds])));
        }
        if (!currentUser?.id || !displayedRole?.id || !notificationModuleId) return;
        dispatch(markAllNotificationsAsReadRequest(currentUser.id, displayedRole.id, notificationModuleId));
    };

    const handleNotificationClick = (notif) => {
        if (notif.unread) {
            markAsRead(notif);
        }
        if (notif.link) {
            navigate(normalizeAppPath(notif.link));
            setIsNotificationsOpen(false);
        }
    };

    const getRoleDefaultRoute = async (role) => {
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');
        const companyId = currentUser?.companyId;

        if (!role?.id || !companyId || !token) {
            return getRolePath(role);
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/sidebar-menus/role/${role.id}/company/${companyId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) return getRolePath(role);

            const menus = await response.json();
            return getFirstVisibleMenuRoute(menus) || getRolePath(role);
        } catch (error) {
            return getRolePath(role);
        }
    };

    const handleRoleChange = async (role) => {
        saveNavigationSelectionForRole(role, currentUser?.id);
        dispatch(setSelectedRole({ ...role, userId: currentUser?.id }));
        const route = await getRoleDefaultRoute(role);
        if (route) {
            navigate(route);
        }
        setIsUserDropdownOpen(false);
    };
    const handleModuleClick = (moduleName) => {
        setIsModulesOpen(false);
    };
    const changeLanguage = (lng) => i18n.changeLanguage(lng);
    const handleLogout = () => {
        dispatch(logout());
        setIsUserDropdownOpen(false);
        navigate('/');
    };
    const handleProfileClick = () => {
        setIsUserDropdownOpen(false);
        navigate('/cards');
    };

    return (
        <div className="upper-bar">
            <div className="upper-bar-content">
                <div className="left-section">
                    {activeModuleLabel && (
                        <div className="active-module-pill" title={t('Module actif')}>
                            <span className="active-module-dot"></span>
                            <span className="active-module-kicker">{t('Module actif')}</span>
                            <span className="active-module-name">{t(activeModuleLabel)}</span>
                        </div>
                    )}
                </div>

                <div className="right-section">


                    {/* ── Language Flags ── */}
                    <div className="language-flags">
                        <button onClick={() => changeLanguage('fr')}
                                className={`flag-button ${i18n.language === 'fr' ? 'active' : ''}`} title="Français">
                            <img src={frenchFlag} alt="Français" className="flag-image"/>
                        </button>
                        <button onClick={() => changeLanguage('en')}
                                className={`flag-button ${i18n.language === 'en' ? 'active' : ''}`} title="English">
                            <img src={englishFlag} alt="English" className="flag-image"/>
                        </button>
                        <button onClick={() => changeLanguage('ar')}
                                className={`flag-button ${i18n.language === 'ar' ? 'active' : ''}`} title="العربية">
                            <img src={arabicFlag} alt="العربية" className="flag-image"/>
                        </button>
                    </div>

                    {/* ── Modules Grid Dropdown ── */}
                    <div className="dropdown-container" ref={modulesRef}>
                        <button className="icon-button modules-button"
                                onClick={() => setIsModulesOpen(!isModulesOpen)} aria-label="Modules">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                                <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                                <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                                <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                        </button>
                        {isModulesOpen && (
                            <div className="dropdown-menu modules-menu">
                                <div className="dropdown-header">
                                    <h3>{t('Modules')}</h3>
                                </div>
                                <div className="modules-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                    {userModulesForSwitcher.length > 0 ? (
                                        userModulesForSwitcher.map((mod) => {
                                            const meta = MODULE_ICONS[mod.code] || {emoji: '📦', bg: '#EDF2FA'};
                                            const isActive = String(mod.id) === String(activeModule?.id) || mod.code === activeModuleCode;
                                            return (
                                                <div
                                                    key={mod.id}
                                                    className={`module-card ${isActive ? 'active' : ''}`}
                                                    style={{'--module-bg': meta.bg}}
                                                    onClick={() => handleModuleSwitcherClick(mod)}
                                                >
                                                    <div className="module-card-content">
                                                        <div className={`module-card-icon ${mod.picture ? 'has-image' : ''}`}
                                                             style={{backgroundColor: meta.bg}}>
                                                            {mod.picture ? (
                                                                <img src={getModuleImageUrl(mod.picture)} alt={mod.name} />
                                                            ) : (
                                                                <span style={{fontSize: 18}}>{meta.emoji}</span>
                                                            )}
                                                        </div>
                                                        <div className="module-card-info">
                                                            <h4 className="module-card-title">{t(mod.name)}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div style={{ padding: '16px', color: '#6b7a99', fontSize: 12, textAlign: 'center', gridColumn: '1/-1' }}>
                                            {t('Aucun module disponible')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* ── Notifications ── */}
                    <div className="dropdown-container" ref={notificationsRef}>
                        <button className="icon-button notification-button"
                                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                                aria-label="Notifications">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"
                                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {unreadCount > 0 && (
                                <span className="notification-badge">{unreadCount}</span>
                            )}
                        </button>
                        {isNotificationsOpen && (
                            <div className="dropdown-menu notifications-menu">
                                <div className="dropdown-header">
                                    <h3>{t('Notifications')}</h3>
                                    {unreadCount > 0 && (
                                        <button className="mark-all-read" onClick={markAllAsRead}>
                                            {t('Tout marquer comme lu')}
                                        </button>
                                    )}
                                </div>
                                <div className="notifications-list">
                                    {notifications.length > 0 ? (
                                        notifications.map((notif) => (
                                            <div key={notif.id}
                                                 className={`notification-item ${notif.unread ? 'unread' : ''}`}
                                                 onClick={() => handleNotificationClick(notif)}
                                                 role="button"
                                                 tabIndex={0}>
                                                <div className="notification-content">
                                                    {notif.unread && <span className="unread-dot"></span>}
                                                    <div className="notification-text">
                                                        {notif.module && <span className="notification-module">{notif.module}</span>}
                                                        <p>{notif.message}</p>
                                                        <span className="notification-time">{notif.time}</span>
                                                    </div>
                                                </div>
                                                {notif.unread && (
                                                    <button className="mark-read-button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                markAsRead(notif);
                                                            }}
                                                            title="Marquer comme lu">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor">
                                                            <polyline points="20 6 9 17 4 12" strokeWidth="2"
                                                                      strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '16px', color: '#6b7a99', fontSize: 13, textAlign: 'center' }}>
                                            {t('Aucune notification')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── User Profile ── */}
                    <div className="dropdown-container-roles" ref={userDropdownRef}>
                        <div className="user-profile" onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}>
                            <div className="user-avatar">
                                {hasProfilePhoto ? (
                                    <img
                                        src={profilePhotoSrc}
                                        alt="Profile"
                                        className="profile-image"
                                        onError={() => setProfileImageFailed(true)}
                                    />
                                ) : (
                                    <span className="user-avatar-initials">{userInitials}</span>
                                )}
                                <span className="status-indicator"></span>
                            </div>
                            <div className="user-info">
                                <span className="user-name">{currentUser?.fullName || 'Utilisateur'}</span>
                                {displayedRole && (
                                    <span className="current-role-badge">{displayedRole.name}</span>
                                )}
                            </div>
                        </div>

                        {isUserDropdownOpen && (
                            <div className="user-dropdown">
                                {userRoles.length > 0 && (
                                    <div className="user-dropdown-section">
                                        <h4 className="user-dropdown-section-title">Rôle :</h4>
                                        <ul className="role-list">
                                            {userRoles.map((role) => (
                                                <li
                                                    key={role.id}
                                                    className={`role-item ${displayedRole?.id === role.id ? 'active' : ''}`}                                                    onClick={() => handleRoleChange(role)}
                                                >
                                                    <div className="role-icon">{getRoleIcon(role.name)}</div>
                                                    <div className="role-item-text">
                                                        <p className="role-name">{role.name}</p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="user-dropdown-section">
                                    <div className="dropdown-menu-item" onClick={handleProfileClick}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor">
                                            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round"
                                                    strokeLinejoin="round"/>
                                        </svg>
                                        <span>Mon profil</span>
                                    </div>
                                    <div className="dropdown-menu-item logout" onClick={handleLogout}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor">
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                                                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <polyline points="16 17 21 12 16 7" strokeWidth="2"
                                                      strokeLinecap="round" strokeLinejoin="round"/>
                                            <line x1="21" y1="12" x2="9" y2="12" strokeWidth="2"
                                                  strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>Se déconnecter</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default UpperBar;
